import {
  TextareaAutosize,
  Typography,
  Button,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import "./AiTools.css";
import { useEffect, useState } from "react";
import { fal } from "@fal-ai/client";
import {
  NEGATIVE_PROMPT,
  FAL_KEY,
  FAL_MODEL,
  URLS,
  FAL_TRANSITION_API,
  AI_VIDEO_TRANSITION_DEFAULT_PROMPT,
} from "../../../constants";
import {
  Cancel,
  CheckCircle,
  Close,
  PhotoFilterRounded,
  SaveAltRounded,
  Videocam,
} from "@mui/icons-material";
import { uvenuFetcher } from "../../../utils/uvenu-fetcher";
import UvDialog from "../../../components/UI/UvDialog";
import SavedPrompts from "./SavedPrompts";
import { COLORS } from "../../../utils/colors";
import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";
// const defaultResponse = {
//   seed: 2339060450,
//   images: [
//     {
//       url: "https://fal.media/files/elephant/Hatyu4nfLL6nFxL5C1Jd6.png",
//       width: 512,
//       height: 512,
//       content_type: "image/jpeg",
//     },
//   ],
//   prompt:
//     "A cute woman with pastel-colored hair and large sparkling eyes, wearing a flowing dress adorned with flowers, standing in a magical forest surrounded by glowing fireflies, pixar.",
//   timings: {
//     inference: 1.654581356793642,
//   },
//   has_nsfw_concepts: [false],
// };
const RenderSnackBar = ({ showToast, toastConfig, onClose }) => {
  const [show, setShow] = useState(showToast);

  useEffect(() => {
    setShow(showToast);
  }, [showToast]);
  return (
    <Snackbar
      open={show}
      autoHideDuration={toastConfig?.duration || 2000}
      onClose={() => onClose()}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert
        severity={toastConfig.type}
        variant="standard"
        icon={
          toastConfig.type === "success" ? (
            <CheckCircle sx={{ color: COLORS.success }} />
          ) : (
            <Cancel sx={{ color: COLORS.error }} />
          )
        }
      >
        {toastConfig.message}
        <>
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => setShow(false)}
            sx={{ ml: 1 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </>
      </Alert>
    </Snackbar>
  );
};
const AiTools = ({
  media,
  editedImage,
  onGenerated,
  eventDetails,
  onVideoGenerated,
}) => {
  const [selectedPrompt, setSelectedPrompt] = useState("");
  const [generatedResponse, setGeneratedResponse] = useState(null);
  const [generatedMedia, setGeneratedMedia] = useState(null);
  const [prompt, setPrompt] = useState(selectedPrompt?.prompt || "");
  const [generating, setGenerating] = useState(null);
  const [refresh, setRefresh] = useState(false);

  const [videoPrompt, setVideoPrompt] = useState(
    AI_VIDEO_TRANSITION_DEFAULT_PROMPT
  );
  const [source, setSource] = useState(null);
  const [hasNsfw, setHasNsfw] = useState(false);

  const [showSavePromptDialog, setShowSavePromptDialog] = useState(false);
  const [promptName, setPromptName] = useState("");

  const [isUseSeed, setIsUseSeedNumber] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedPromptRefImages, setSelectedPromptRefImages] = useState([]);
  const [referenceImages, setReferenceImages] = useState(
    selectedPrompt?.sourceImageUrls || []
  );
  const [videoFirstImage, setVideoFirstImage] = useState(source);
  const [videoEndImage, setVideoEndImage] = useState(null);
  const [aspectRatio, setAspectRatio] = useState("1:1");

  useEffect(() => {
    if (editedImage) {
      setSource(editedImage);
    } else if (media?.mediaFileUrl) {
      setSource(media?.mediaFileUrl);
    }
  }, [media, editedImage]);

  useEffect(() => {
    if (generatedMedia) {
      setGenerating(false);
    }
  }, [generatedMedia]);

  useEffect(() => {
    setReferenceImages(selectedPrompt?.sourceImageUrls || []);
  }, [selectedPrompt]);

  const handleSelectPrompt = (savedPrompt) => {
    setSelectedPrompt(savedPrompt);
    setPrompt(savedPrompt.prompt);
    setIsUseSeedNumber(false);
  };

  const handleSavePromptClick = async () => {
    if (!promptName) {
      alert("Please enter a prompt name");
      return;
    }

    try {
      const response = await uvenuFetcher({
        method: "POST",
        url: URLS.SAVE_PROMPT,
        body: {
          eventId: eventDetails.event_id,
          prompt,
          name: promptName,
          seed: generatedResponse?.seed,
          exampleImageUrl: generatedResponse?.images[0].url,
          userId: eventDetails.loggedInUserObj.email,
          apiId: "nano-banana",
          sourceImageUrls: source ? [source] : [],
        },
        headers: {
          token: `${eventDetails.loggedInUserObj.token}`,
        },
      });
      if (response.statusCode === 200 || response.statusCode === 201) {
        setRefresh(!refresh);
        setPromptName("");
        setPrompt("");
        setSelectedPrompt(response.json.prompt);
        setShowSavePromptDialog(false);
      } else if (response.statusCode === 409) {
        const duplicateOf = response.json.prompt;
        alert(
          `Prompt with name "${duplicateOf.name}" and description "${duplicateOf.prompt}" already exists.`
        );
        return;
      } else {
        console.log(response.json.message);
        return "error: " + response.json.message;
      }
    } catch (error) {
      console.log(error);
    } finally {
      setPromptName("");
      setShowSavePromptDialog(false);
    }
  };

  const handleGenerateClick = async () => {
    if (!prompt) {
      alert("Please enter a prompt");
      return;
    }
    if (!source) {
      alert("Please upload a media file");
      return;
    }
    fal.config({
      credentials: FAL_KEY,
    });
    setGeneratedMedia("");
    setGenerating(true);
    try {
      const input = {
        prompt,
        image_urls: [source, ...selectedPromptRefImages],
        negative_prompt: NEGATIVE_PROMPT,
        aspect_ratio: aspectRatio,
      };

      const result = await fal.subscribe(FAL_MODEL, {
        input,
        logs: true,
        onQueueUpdate: (update) => {
          if (update.status === "IN_PROGRESS") {
            update.logs.map((log) => log.message).forEach(console.log);
          }
        },
      });

      if (result && result.data) {
        setGeneratedResponse(result.data);
        setGeneratedMedia(result.data.images[0].url);
        onGenerated(result.data.images[0]);
        if (
          result.data.has_nsfw_concepts &&
          result.data.has_nsfw_concepts.indexOf(true) !== -1
        ) {
          setHasNsfw(true);
        } else {
          setHasNsfw(false);
        }
        setGenerating(false);
      }
    } catch (error) {
      alert(error);
      setGenerating(false);
    }
  };
  const handleCreateVideoClick = async () => {
    setIsGeneratingVideo(true);
    setShowToast(true);
    fal.config({
      credentials: FAL_KEY,
    });
    const result = await fal.subscribe(FAL_TRANSITION_API, {
      input: {
        prompt: videoPrompt,
        first_image_url: videoFirstImage,
        end_image_url: videoEndImage,
        duration: "5",
        resolution: "540p",
        aspect_ratio: "1:1",
        seed: 4329664,
      },
      logs: true,
      onQueueUpdate: (update) => {
        if (update.status === "IN_PROGRESS") {
          update.logs.map((log) => log.message).forEach(console.log);
        }
      },
    });

    if (result && result.data) {
      setGeneratedResponse(result.data);
      onVideoGenerated(result.data.video);
    }
    setIsGeneratingVideo(false);
  };
  const onReferenceImageSelection = (url) => {
    if (videoEndImage) {
      setVideoFirstImage(url);
      if (selectedPromptRefImages.includes(url)) {
        setSelectedPromptRefImages([]);
      } else {
        setSelectedPromptRefImages([url]);
      }
    } else {
      if (selectedPromptRefImages.includes(url)) {
        setSelectedPromptRefImages(
          selectedPromptRefImages.filter((img) => img !== url)
        );
      } else {
        setSelectedPromptRefImages([...selectedPromptRefImages, url]);
      }
    }
  };
  return (
    <>
      <div className="flex ait-wrapper">
        <div className="ait-saved-prompts">
          <SavedPrompts
            handleSelectPrompt={handleSelectPrompt}
            eventDetails={eventDetails}
            apiId="nano-banana"
            key={refresh}
          />
        </div>
        <div className="ait-main-container flex-1 flex-col">
          <div className="flex flex-1">
            <div className="flex-2 flex flex-col">
              <TextareaAutosize
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="ait-prompt flex-1"
                placeholder="Enter Prompt"
                minRows={3}
                name="prompt"
              ></TextareaAutosize>
              {generatedMedia && (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography className="media-label" sx={{ my: 1 }}>
                    Video Prompt
                  </Typography>
                  <TextareaAutosize
                    value={videoPrompt}
                    onChange={(e) => setVideoPrompt(e.target.value)}
                    className="ait-prompt"
                    placeholder="Enter Video Prompt"
                    minRows={2}
                    style={{
                      backgroundColor: "transparent",
                    }}
                  ></TextareaAutosize>
                </div>
              )}
              {referenceImages.length > 0 && (
                <div className="flex flex-col justify-center items-center text-white">
                  <Typography className="media-label" sx={{ my: 1 }}>
                    {videoEndImage
                      ? "Select first Image for video"
                      : "Select Reference Images"}
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    {referenceImages.map((url, idx) => (
                      <div
                        key={url}
                        className={
                          selectedPromptRefImages.includes(url)
                            ? "prompt-ref-image prompt-ref-image-selected"
                            : "prompt-ref-image"
                        }
                      >
                        <img
                          src={url}
                          alt={`source-img-${idx}`}
                          onClick={() => {
                            onReferenceImageSelection(url);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex flex-col justify-center items-center text-white">
                <Typography className="media-label" sx={{ my: 1 }}>
                  Aspect Ratio
                </Typography>
                <select
                  value={aspectRatio}
                  onChange={(e) => setAspectRatio(e.target.value)}
                  style={{
                    width: "200px",
                    padding: "8px",
                    backgroundColor: "rgb(13, 14, 19)",
                    border: "1px solid rgb(13, 14, 19, 0.6)",
                    borderRadius: "4px",
                    color: "white",
                    marginBottom: "12px",
                    width: "100%",
                  }}
                >
                  <option value="1:1">Square (1:1)</option>
                  <option value="16:9">Landscape (16:9)</option>
                  <option value="9:16">Portrait (9:16)</option>
                  <option value="4:3">Classic (4:3)</option>
                  <option value="3:4">Portrait Classic (3:4)</option>
                  <option value="3:2">Landscape (3:2)</option>
                  <option value="2:3">Portrait (2:3)</option>
                  <option value="5:4">Classic Wide (5:4)</option>
                  <option value="4:5">Classic Tall (4:5)</option>
                  <option value="21:9">Ultrawide (21:9)</option>
                </select>
              </div>
              {generating || !selectedPrompt?.seed ? null : (
                <FormControlLabel
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "white",
                  }}
                  control={
                    <Checkbox
                      checked={isUseSeed && selectedPrompt?.seed}
                      onChange={(e) => {
                        setIsUseSeedNumber(e.target.checked);
                      }}
                      name="useSeedNumber"
                      color="primary"
                    />
                  }
                  label="Generate Exact Style"
                />
              )}
            </div>
            <div className="ai-tools-container">
              <div className="ait-media-container">
                <div className="source-media ait-media-box">
                  <Typography className="media-label">Before</Typography>
                  <div className="ait-media">
                    <img
                      src={source}
                      alt="source-media"
                      className="h-full object-contain w-auto"
                    />
                  </div>
                  {generatedMedia ? (
                    source === videoFirstImage ? (
                      <>
                        {" "}
                        <Typography variant="body2" color="gray">
                          This image will be used as first image for video
                        </Typography>
                        <Button
                          onClick={() => {
                            setVideoFirstImage(null);
                          }}
                        >
                          Reset
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ marginTop: "8px", color: "white" }}
                        onClick={() => {
                          setVideoFirstImage(source);

                          setSelectedPromptRefImages([]);
                        }}
                      >
                        Use this as first image for video
                      </Button>
                    )
                  ) : null}
                </div>
                <div className="generated-media ait-media-box">
                  <Typography className="media-label">After</Typography>
                  <div className="ait-media relative">
                    {generating ? <CircularProgress /> : null}

                    {generatedMedia ? (
                      <>
                        <img
                          src={generatedMedia}
                          alt="generted"
                          className="h-full object-contain w-auto"
                        />
                        <IconButton
                          aria-label="clear-generated-media"
                          size="small"
                          onClick={() => setGeneratedMedia(null)}
                          title="Clear generated image to create new"
                          style={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            background: "rgba(255,0,0,0.5)",
                            color: "white",
                            zIndex: 2,
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </>
                    ) : null}
                    {hasNsfw && <p>NSFW Content</p>}
                  </div>
                  {generatedMedia ? (
                    generatedMedia === videoEndImage ? (
                      <>
                        <Typography variant="body2" color="gray">
                          This image will be used as end image for video{" "}
                        </Typography>
                        <Button
                          onClick={() => {
                            setVideoEndImage(null);
                          }}
                        >
                          Reset
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ marginTop: "8px", color: "white" }}
                        onClick={() => {
                          setVideoEndImage(generatedMedia);
                          if (selectedPromptRefImages.length > 1) {
                            setSelectedPromptRefImages([]);
                          }
                        }}
                      >
                        Use this as end image for video
                      </Button>
                    )
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="generate-container">
            <Button
              disabled={generating}
              variant="contained"
              sx={{ color: "white" }}
              startIcon={<PhotoFilterRounded />}
              onClick={handleGenerateClick}
            >
              {generatedMedia ? "Re-Generate" : "Generate"}
            </Button>
            <Button
              disabled={generating !== false || !prompt}
              variant="contained"
              sx={{ color: "white", marginLeft: "16px" }}
              startIcon={<SaveAltRounded />}
              onClick={() => {
                setShowSavePromptDialog(true);
              }}
            >
              Save Prompt
            </Button>
            <Button
              disabled={videoFirstImage && videoEndImage ? false : true}
              variant="contained"
              sx={{ color: "white", marginLeft: "16px" }}
              startIcon={<Videocam />}
              title={
                videoFirstImage && videoEndImage
                  ? "Create video from selected images"
                  : "Select first and end images to create video"
              }
              onClick={() => {
                setShowConfirmation(true);
              }}
            >
              Create Video
            </Button>
          </div>
        </div>
      </div>
      <UvDialog
        show={showSavePromptDialog}
        onClose={() => {
          setPromptName("");
          setShowSavePromptDialog(false);
        }}
        onSubmit={handleSavePromptClick}
        title="Save Prompt"
        submitButtonLable="Save"
      >
        <TextField
          label="Prompt Name"
          variant="outlined"
          fullWidth
          value={promptName}
          onChange={(e) => {
            setPromptName(e.target.value);
          }}
          margin="normal"
          sx={{ marginBottom: "20px" }}
        />
      </UvDialog>
      <RenderSnackBar
        showToast={showToast}
        onClose={() => setShowToast(false)}
        toastConfig={{
          type: "success",
          message:
            "Creating your video — this may take a moment. You’ll find it in Production Media when it’s done.",
          duration: 5000,
        }}
      />
      <ConfirmationDialog
        title="Create Video Confirmation"
        confirmButtonText="Yes Create"
        message={`Are you sure you want to create a video?`}
        show={showConfirmation}
        onConfirm={() => {
          handleCreateVideoClick();
          setShowConfirmation(false);
        }}
        onClose={() => {
          setShowConfirmation(false);
        }}
      />
    </>
  );
};
export default AiTools;
